import React from "react";
import { ShieldCheck } from "lucide-react";
import { fmtMoney, fmtRate } from "./api";

export function LenderAvatar({ logoUrl, displayName, isMasked }) {
  // Masked tiers get a generic shield icon. Real lenders show their logo if
  // available, else a clean initial-circle in brand-navy.
  if (isMasked) {
    return (
      <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
        <ShieldCheck size={22} className="text-brand-navy" strokeWidth={2.25} />
      </div>
    );
  }
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        className="w-12 h-12 rounded-xl object-contain bg-white border border-slate-200 p-1 shrink-0"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    );
  }
  const initials =
    (displayName || "")
      .split(/[\s.&-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "•";
  return (
    <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center shrink-0">
      <span className="text-sm font-bold tracking-tight font-display">{initials}</span>
    </div>
  );
}

export function fmtRateHoldDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  } catch (_e) {
    return iso;
  }
}

function RateInfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-1.5 last:border-b-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-bold text-brand-navy tabular-nums">{value}</span>
    </div>
  );
}

export function LenderResultCard({ match, top, onInquire, delay = 0, paymentLabel = "/mo", paymentAmount, variant = "default" }) {
  const isMasked = !!match.is_masked;
  const subline = isMasked ? match.channel_label : match.term;
  const payAmt = paymentAmount != null ? paymentAmount : match.monthly_payment;
  const isFeatured = variant === "featured";
  const shellClasses = isFeatured
    ? "relative bg-emerald-50 border-2 border-emerald-300 hover:border-emerald-400 transition-colors rounded-2xl p-5 sm:p-6 overflow-hidden animate-fade-up"
    : "relative bg-white border-2 border-slate-200 hover:border-brand-navy/40 transition-colors rounded-2xl p-5 sm:p-6 overflow-hidden animate-fade-up";
  return (
    <article
      className={shellClasses}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`reveal-card-${match.lender_id}`}
    >
      {top && (
        <div className="absolute top-0 right-0 bg-brand-red text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-bl-xl font-body">
          Lowest Rate
        </div>
      )}

      <div className="flex items-start gap-5 sm:gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 mb-5">
            <LenderAvatar
              logoUrl={match.logo_url}
              displayName={match.lender_name}
              isMasked={isMasked}
            />
            <div className="min-w-0">
              <div className="text-base sm:text-lg font-bold text-brand-navy font-display leading-tight truncate" data-testid="lender-card-name">
                {match.lender_name}{match.program_name ? <span className="font-normal"> — {match.program_name}</span> : null}
              </div>
              <div className="text-[11px] text-slate-500 font-body mt-0.5 truncate">
                {subline}
              </div>
            </div>
          </div>

          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 font-body mb-2">
            Rate info
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2 text-[13px] font-body">
            <RateInfoRow label="Monthly prepayment" value={`${match.monthly_prepayment_pct}%`} />
            <RateInfoRow label="Lump sum prepayment" value={`${match.lump_sum_prepayment_pct}%`} />
            <RateInfoRow label="Rate hold until" value={fmtRateHoldDate(match.rate_hold_until)} />
            <RateInfoRow
              label="Pre-approval"
              value={match.pre_approval_available ? "Available" : "Not available"}
            />
            {match.prepay_note && <RateInfoRow label="Prepayment terms" value={match.prepay_note} />}
          </div>
        </div>

        <div className="w-[140px] sm:w-[160px] shrink-0">
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-4 text-center mb-3">
            <div className="text-3xl sm:text-4xl font-bold tracking-tighter font-display text-brand-navy leading-none">
              {fmtRate(match.rate_to_client)}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs sm:text-sm text-slate-500 font-body tabular-nums">
              {fmtMoney(payAmt)}{paymentLabel}
            </div>
          </div>
          <button
            type="button"
            onClick={onInquire}
            data-testid={`reveal-inquire-${match.lender_id}`}
            className="w-full bg-brand-red text-white rounded-xl py-3 font-bold text-sm font-body hover:bg-brand-red-hover transition-colors"
          >
            Inquire
          </button>
        </div>
      </div>
    </article>
  );
}
